// Schedule page for Sunlight Grounds Care Client Portal
import { useState, useEffect, useCallback } from 'react';
import Header from '@/components/layout/Header';
import api from '@/api/client';
import { useProperty } from '@/hooks/useProperty';
import { usePoll } from '@/hooks/usePoll';

// Convert "07:00" or "15:00" to "7:00 AM" or "3:00 PM" PST
const formatTime12hr = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

export default function Schedule({ user }) {
  const { selectedProperty } = useProperty();
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [weather, setWeather] = useState(null);
  const [upcomingRoutes, setUpcomingRoutes] = useState([]);
  const [pastRoutes, setPastRoutes] = useState([]);
  const [pastServices, setPastServices] = useState([]);
  const [liveSchedule, setLiveSchedule] = useState([]);

  // Poll staff status every 15 seconds for real-time ETA
  const fetchStaffStatus = useCallback(async () => {
    if (!selectedProperty) return null;
    return api.getStaffStatus(selectedProperty.id);
  }, [selectedProperty]);

  const { data: staffStatus } = usePoll(fetchStaffStatus, 15000, !!selectedProperty);

  // Poll landscape schedule every 20 seconds — detects route status changes (en_route, on_property)
  const fetchLiveSchedule = useCallback(async () => {
    if (!selectedProperty) return null;
    const data = await api.getLandscapeSchedule(selectedProperty.id);
    return (data.schedule || []).filter(s => s.property_id === selectedProperty.id);
  }, [selectedProperty]);

  const { data: polledSchedule } = usePoll(fetchLiveSchedule, 20000, !!selectedProperty);

  // Keep liveSchedule in sync with polled data
  useEffect(() => {
    if (polledSchedule) setLiveSchedule(polledSchedule);
  }, [polledSchedule]);

  // Match a route's property list against the selected property by name
  const matchesProperty = useCallback((routeProperties, clientProperty) => {
    if (!clientProperty) return false;
    const clientName = (clientProperty.name || '').toLowerCase().trim();
    const clientAddr = (clientProperty.address || '').toLowerCase().trim();
    
    return routeProperties.some(rp => {
      const routeName = (rp.property_name || '').toLowerCase().trim();
      if (!routeName) return false;
      // Check if route name is contained in client name/address or vice versa
      return clientName.includes(routeName) || clientAddr.includes(routeName) ||
             routeName.includes(clientName);
    });
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!selectedProperty) {
        setLoading(false);
        return;
      }
      
      try {
        const results = await Promise.allSettled([
          api.getLandscapeSchedule(selectedProperty.id),
          api.getWeather(),
          api.getGroundsCareRoutes(),
          api.getLandscapePhotos(selectedProperty.id),
          api.getDashboardData(selectedProperty.id)
        ]);
        
        if (results[0].status === 'fulfilled') {
          const data = results[0].value;
          // Filter schedule items to only the selected property
          const propSchedule = (data.schedule || []).filter(
            s => s.property_id === selectedProperty.id
          );
          // Sort by date ascending and separate upcoming from past
          const today = new Date().toISOString().split('T')[0];
          const upcomingSched = propSchedule.filter(s => s.date >= today).sort((a, b) => a.date.localeCompare(b.date));
          setSchedule({
            upcoming: upcomingSched, // all upcoming, not just first
            tasks: propSchedule,
            last_service: data.properties?.find(p => p.id === selectedProperty.id)?.last_service_date || null,
            properties: data.properties || []
          });
        }
        if (results[1].status === 'fulfilled') setWeather(results[1].value);
        
        // Filter routes that include the selected property
        if (results[2].status === 'fulfilled') {
          const allRoutes = results[2].value || [];
          const matched = allRoutes
            .filter(route => matchesProperty(route.properties || [], selectedProperty));
          
          const today = new Date().toISOString().split('T')[0];
          const future = matched
            .filter(r => r.date >= today)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
          const past = matched
            .filter(r => r.date < today)
            .sort((a, b) => new Date(b.date) - new Date(a.date)); // newest first
          
          setUpcomingRoutes(future);
          setPastRoutes(past);
        }

        // Get shared_photo_dates from dashboard data
        // null = dashboard failed (show all as fallback), [] = nothing shared, [...dates] = filter to these
        let sharedDates = null;
        if (results[4].status === 'fulfilled') {
          const rawShared = results[4].value?.shared_photo_dates;
          if (Array.isArray(rawShared)) {
            sharedDates = rawShared;
          }
        }

        // Derive past services from photo dates — respect manager sharing
        if (results[3].status === 'fulfilled') {
          const data = results[3].value || {};
          let propPhotos = (data.photos || []).filter(p => p.property_id === selectedProperty.id);
          
          if (sharedDates !== null) {
            if (sharedDates.length > 0) {
              propPhotos = propPhotos.filter(p => {
                const photoDate = (p.uploaded_at || '').slice(0, 10);
                return sharedDates.includes(photoDate);
              });
            } else {
              propPhotos = []; // Empty shared_photo_dates = nothing shared
            }
          }
          // sharedDates === null → dashboard failed, show all as fallback

          const byDate = {};
          propPhotos.forEach(p => {
            const date = (p.uploaded_at || '').slice(0, 10);
            if (!date) return;
            if (!byDate[date]) byDate[date] = { date, count: 0, spaces: new Set() };
            byDate[date].count++;
            if (p.space_name) byDate[date].spaces.add(p.space_name.trim());
          });
          const services = Object.values(byDate)
            .sort((a, b) => b.date.localeCompare(a.date))
            .map(s => ({ ...s, spaces: [...s.spaces] }));
          setPastServices(services);
        }
      } catch (err) {
        console.error('Failed to load schedule data:', err);
      } finally {
        setLoading(false);
      }
    };

    setUpcomingRoutes([]);
    setPastRoutes([]);
    setPastServices([]);
    loadData();
  }, [selectedProperty, matchesProperty]);

  const getStatusStyle = (status) => {
    const s = status?.toLowerCase().replace(/_/g, ' ');
    switch (s) {
      case 'en route':
      case 'navigating':
        return { bg: 'bg-gold-pale', dot: 'bg-gold', pill: 'bg-gold-light text-[#996B00]', label: 'En Route' };
      case 'on property':
      case 'on site':
      case 'in progress':
        return { bg: 'bg-blue-50', dot: 'bg-blue-600', pill: 'bg-blue-100 text-blue-700', label: 'On Property' };
      case 'completed':
        return { bg: 'bg-green-light', dot: 'bg-green', pill: 'bg-green-light text-green', label: 'Completed' };
      default:
        return { bg: 'bg-green-light', dot: 'bg-green', pill: 'bg-green-light text-green', label: status || 'Scheduled' };
    }
  };

  // Normalize status string for comparison
  const norm = (s) => (s || '').toLowerCase().replace(/_/g, ' ').trim();

  // staffStatus may be an array [] or an object — normalize
  const activeStaff = Array.isArray(staffStatus)
    ? (staffStatus.length > 0 ? staffStatus[0] : null)
    : (staffStatus && typeof staffStatus === 'object' && staffStatus.status ? staffStatus : null);

  // Detect en-route or on-property from live schedule items
  const activeScheduleItem = liveSchedule.find(s => 
    ['en_route', 'en route', 'navigating', 'on_property', 'on property', 'on site', 'in_progress', 'in progress']
      .includes(norm(s.status))
  );

  // Determine the "crew state" — priority: en_route > on_property > scheduled > none
  const isEnRoute = norm(activeStaff?.status) === 'en route' || norm(activeStaff?.status) === 'navigating' ||
                    norm(activeScheduleItem?.status) === 'en route' || norm(activeScheduleItem?.status) === 'en_route' ||
                    norm(activeScheduleItem?.status) === 'navigating';
  const isOnProperty = norm(activeStaff?.status) === 'on property' || norm(activeStaff?.status) === 'on site' ||
                       norm(activeStaff?.status) === 'in progress' ||
                       norm(activeScheduleItem?.status) === 'on property' || norm(activeScheduleItem?.status) === 'on_property' ||
                       norm(activeScheduleItem?.status) === 'in progress' || norm(activeScheduleItem?.status) === 'in_progress';

  // Build ETA info from whichever source provides it
  const etaSource = activeStaff || activeScheduleItem || {};
  const etaArrival = etaSource.arrival_time || etaSource.eta_arrival || etaSource.eta || null;
  const etaMinutes = etaSource.eta_minutes || etaSource.eta_min || null;
  const etaStaff = etaSource.staff_name || activeScheduleItem?.staff_name || activeStaff?.staff_name || null;
  const etaService = etaSource.service || etaSource.service_type || activeScheduleItem?.service_type || null;

  const statusStyle = getStatusStyle(
    isEnRoute ? 'en route' : isOnProperty ? 'on property' : activeStaff?.status
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-light">
        <Header title="Schedule" property={selectedProperty} />
        <div className="p-4">
          <div className="sg-card p-4 animate-pulse mb-4">
            <div className="h-4 bg-slate/20 rounded w-32 mb-3"></div>
            <div className="h-3 bg-slate/20 rounded w-24"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-light pb-24" data-testid="schedule-page">
      <Header title="Schedule" property={selectedProperty} />
      
      {/* Crew ETA Banner — real-time status with ETA */}
      {(isEnRoute || isOnProperty) ? (
        <div 
          className={`mx-4 sm:mx-6 md:mx-8 mt-4 p-3.5 rounded-xl ${statusStyle.bg} max-w-4xl md:mx-auto md:mt-6`}
          data-testid="crew-eta-banner"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-2.5 h-2.5 rounded-full ${statusStyle.dot} ${isEnRoute ? 'animate-pulse' : ''}`}></div>
              <div>
                {isEnRoute ? (
                  <>
                    <p className="font-dm text-xs font-bold text-navy">
                      {etaStaff || 'Crew'} is on the way
                      {etaArrival ? ` · Arriving at ${etaArrival}` : ''}
                    </p>
                    <p className="font-dm text-[10px] text-[#996B00] mt-0.5">
                      {etaMinutes ? `In ~${etaMinutes} min · ` : ''}
                      {etaService || 'Grounds Care'}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-dm text-xs font-bold text-navy">
                      {etaStaff || 'Crew'} is on property
                    </p>
                    <p className="font-dm text-[10px] text-blue-600 mt-0.5">
                      {etaService || 'Grounds Care'} · In progress
                    </p>
                  </>
                )}
              </div>
            </div>
            <span className={`text-[9px] font-dm font-medium px-2.5 py-1 rounded-full ${statusStyle.pill}`}>
              {statusStyle.label}
            </span>
          </div>
        </div>
      ) : upcomingRoutes.length > 0 ? (
        <div 
          className="mx-4 sm:mx-6 md:mx-8 mt-4 p-3.5 rounded-xl bg-green-light max-w-4xl md:mx-auto md:mt-6"
          data-testid="crew-eta-banner"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 rounded-full bg-green"></div>
              <div>
                <p className="font-dm text-xs font-bold text-navy">
                  Crew scheduled · {new Date(upcomingRoutes[0].date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </p>
                <p className="font-dm text-[10px] text-green mt-0.5">
                  {upcomingRoutes[0].staff_name} · {formatTime12hr(upcomingRoutes[0].start_time)} – {formatTime12hr(upcomingRoutes[0].end_time)} PST
                </p>
              </div>
            </div>
            <span className="text-[9px] font-dm font-medium px-2.5 py-1 rounded-full capitalize bg-green-light text-green">
              {upcomingRoutes[0].status}
            </span>
          </div>
        </div>
      ) : activeStaff ? (
        <div 
          className={`mx-4 sm:mx-6 md:mx-8 mt-4 p-3.5 rounded-xl ${statusStyle.bg} max-w-4xl md:mx-auto md:mt-6`}
          data-testid="crew-eta-banner"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-2.5 h-2.5 rounded-full ${statusStyle.dot}`}></div>
              <div>
                <p className="font-dm text-xs font-bold text-navy">
                  {activeStaff.staff_name || 'Crew'} · {activeStaff.arrival_time || activeStaff.date}
                </p>
                <p className="font-dm text-[10px] text-green mt-0.5">
                  {activeStaff.service} {activeStaff.eta_minutes ? `· ~${activeStaff.eta_minutes} min` : ''}
                </p>
              </div>
            </div>
            <span className={`text-[9px] font-dm font-medium px-2.5 py-1 rounded-full capitalize ${statusStyle.pill}`}>
              {activeStaff.status}
            </span>
          </div>
        </div>
      ) : (
        <div 
          className="mx-4 sm:mx-6 md:mx-8 mt-4 p-3.5 rounded-xl bg-slate-light border border-black/[0.06] max-w-4xl md:mx-auto md:mt-6"
          data-testid="crew-eta-empty"
        >
          <div className="flex items-center gap-3">
            <div className="w-2.5 h-2.5 rounded-full bg-slate/30"></div>
            <div>
              <p className="font-dm text-xs font-medium text-navy">No crew currently scheduled</p>
              <p className="font-dm text-[10px] text-slate mt-0.5">We'll notify you when your next visit is confirmed</p>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 pt-3 sm:p-6 md:p-8 max-w-4xl mx-auto">
        {/* Upcoming Tasks — show all scheduled visits */}
        {schedule?.upcoming?.length > 0 ? (
          <>
            <p className="sg-label mb-2">Scheduled visits</p>
            {schedule.upcoming.map((visit, idx) => (
              <div className="sg-card mb-3" key={visit.id || idx} data-testid={`visit-card-${idx}`}>
                <div className="px-4 py-3 border-b border-black/[0.08]">
                  <div className="flex items-center justify-between">
                    <span className="font-dm text-xs font-bold text-navy">
                      {new Date(visit.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </span>
                    <span className="text-[9px] font-dm font-medium px-2.5 py-1 rounded-full capitalize bg-green-light text-green">
                      {visit.status}
                    </span>
                  </div>
                </div>
                <div className="sg-info-row">
                  <span className="text-slate">Service</span>
                  <span className="font-medium text-navy">{visit.service_type || 'Grounds Care'}</span>
                </div>
                <div className="sg-info-row">
                  <span className="text-slate">Crew</span>
                  <span className="font-medium text-navy">{visit.staff_name}</span>
                </div>
                <div className="sg-info-row">
                  <span className="text-slate">Time</span>
                  <span className="font-medium text-navy">{formatTime12hr(visit.start_time)} – {formatTime12hr(visit.end_time)} PST</span>
                </div>
                <div className="sg-info-row border-b-0">
                  <span className="text-slate">Status</span>
                  <span className="inline-block text-[9px] font-dm font-medium px-2.5 py-1 rounded-full capitalize bg-green-light text-green">
                    {visit.status}
                  </span>
                </div>
              </div>
            ))}
          </>
        ) : upcomingRoutes.length > 0 ? (
          <>
            <p className="sg-label mb-2">Scheduled visits</p>
            {upcomingRoutes.map((route, idx) => (
              <div className="sg-card mb-3" key={route.id || idx} data-testid={`route-card-${idx}`}>
                <div className="px-4 py-3 border-b border-black/[0.08]">
                  <div className="flex items-center justify-between">
                    <span className="font-dm text-xs font-bold text-navy">
                      {new Date(route.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </span>
                    <span className="text-[9px] font-dm font-medium px-2.5 py-1 rounded-full capitalize bg-green-light text-green">
                      {route.status}
                    </span>
                  </div>
                </div>
                <div className="sg-info-row">
                  <span className="text-slate">Crew</span>
                  <span className="font-medium text-navy">{route.staff_name}</span>
                </div>
                <div className="sg-info-row">
                  <span className="text-slate">Time</span>
                  <span className="font-medium text-navy">{formatTime12hr(route.start_time)} – {formatTime12hr(route.end_time)} PST</span>
                </div>
                {route.notes && (
                  <div className="sg-info-row border-b-0">
                    <span className="text-slate">Notes</span>
                    <span className="font-medium text-navy">{route.notes}</span>
                  </div>
                )}
              </div>
            ))}
          </>
        ) : (
          <>
            <p className="sg-label mb-2">Upcoming tasks</p>
            <div className="sg-card mb-4 p-4" data-testid="upcoming-tasks-empty">
              <p className="font-dm text-xs text-slate text-center">No upcoming visits scheduled</p>
              <p className="font-dm text-[10px] text-slate/60 text-center mt-1">Your next service will appear here once confirmed</p>
            </div>
          </>
        )}

        {/* Weather Widget — Mon–Sat, compact, above past services */}
        <div className="bg-navy rounded-2xl px-3 py-2.5 mb-4" data-testid="weather-widget">
          {weather ? (
            <>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2.5">
                  <span className="text-2xl">{weather.icon}</span>
                  <div>
                    <p className="font-cormorant text-[22px] font-semibold text-white leading-none">
                      {weather.temp}°F
                    </p>
                    <p className="font-dm text-[9px] text-white/50 mt-0.5">
                      {weather.condition} · {weather.humidity}% · {weather.wind} mph
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-white/[0.10] pt-1.5">
                <div className="flex justify-between">
                  {weather.forecast.map((day, idx) => (
                    <div key={idx} className="text-center flex-1">
                      <p className="text-[9px] font-dm font-semibold text-white/60 uppercase">{day.day}</p>
                      <p className="text-[8px] font-dm text-white/35 leading-tight">{day.date}</p>
                      <span className="text-xs block my-0.5">{day.icon}</span>
                      <p className="text-[9px] font-dm font-bold text-white">{day.temp}°</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2.5">
              <span className="text-xl opacity-40">🌤️</span>
              <div>
                <p className="font-dm text-[11px] text-white/65">Weather data unavailable</p>
                <p className="font-dm text-[9px] text-white/40 mt-0.5">Check back later for forecast info</p>
              </div>
            </div>
          )}
        </div>

        {/* Past Services — derived from photo delivery dates */}
        {pastServices.length > 0 && (
          <>
            <p className="sg-label pt-2 mb-2">Past services</p>
            {pastServices.map((svc, idx) => (
              <div className="sg-card mb-3" key={svc.date} data-testid={`past-service-${idx}`}>
                <div className="px-4 py-3 border-b border-black/[0.08]">
                  <div className="flex items-center justify-between">
                    <span className="font-dm text-xs font-bold text-navy">
                      {new Date(svc.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    <span className="text-[9px] font-dm font-medium px-2.5 py-1 rounded-full bg-green-light text-green">
                      Completed
                    </span>
                  </div>
                </div>
                <div className="sg-info-row">
                  <span className="text-slate">Areas serviced</span>
                  <span className="font-medium text-navy text-right text-[11px] max-w-[60%]">{svc.spaces.join(', ') || 'General'}</span>
                </div>
                <div className="sg-info-row border-b-0">
                  <span className="text-slate">Photos taken</span>
                  <span className="font-medium text-navy">{svc.count}</span>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
